// Admin — Device by Unique Number.
//
// Replaces the old Windows "Devices" form for the one job that matters day to
// day: look a device up, and if it is not assigned, attach it to a user, a
// location and a room.
//
// The old form put ~60 fields on one screen and left the operator to work out
// which mattered. This shows what the device IS, then walks the assignment in
// the order the decisions actually depend on each other:
//
//     Master User  ->  Location (theirs, or new)  ->  Room (theirs, new, or common)
//
// Each step is disabled until the one before it is answered, because a location
// list means nothing before a user is chosen and a room list means nothing
// before a location is.
//
// Super Admin only — it writes ownership.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasNoHeartbeat } from '../../../core/devices/deviceStatus';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Divider, Chip, Alert, Tooltip, IconButton,
  CircularProgress, Autocomplete, Table, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getOtaBinFiles } from '../../../core/api/api';
import { mapDeviceToBinFile, servedOtaUrl, knownBuildSha } from '../../../core/devices/otaBinFiles';
import { askVersion, matchesBuild } from '../../../core/devices/firmwareVersion';
import { parseServerTime } from '../../../core/devices/firmwareInfo';
import {
  setPendingCommand, PENDING_UPDATE_COMMAND, isUpdateCommand,
  markFirmwareUpdate, clearFirmwareUpdateMark, UPDATE_MARK_COLUMN,
} from '../../../core/devices/firmwareUpdate';
import {
  startOtaAndWatch, otaOutcome, otaPayload, OTA_ACK, OTA_SILENCE_MS,
} from '../../../core/devices/otaProgress';
import { useApp } from '../../../core/state/AppContext';
import {
  findDevice, getAllUsers, userLabel, locationsOf, roomsOf, devicesIn,
  createLocation, createRoom, createUser, assignDevice, unassignDeviceFully,
  setDeviceType as saveDeviceType,
  deviceUserIds, grantUserAccess, revokeUserAccess,
  COMMON_ROOM_ID,
} from '../../../core/devices/deviceAdmin';
import { getDeviceTypes, renameRoom } from '../../../core/api/api';
import { pickerOptions } from '../../../core/utils/search';
import DeviceWifiPanel from '../components/DeviceWifiPanel';
// The same card the room grid draws — see the Controls panel below for why the
// admin screen reuses it rather than growing its own test buttons.
import DeviceControlCard from '../components/DeviceControlCard';
import { FIT_XS } from '../../shared/fitDialog';

const NEW = '__new__';

/*
 * ── Where the last device went ─────────────────────────────────────────────
 *
 * Commissioning is a BATCH: a box of thirty switches all going into the same
 * flat, entered one unique number after another. Re-picking the same owner, the
 * same location and the same room thirty times is the actual work of the screen,
 * and the one part a computer should be doing.
 *
 * So a successful assignment is remembered and offered as the starting point
 * for the NEXT unassigned device. In localStorage rather than in state, because
 * a bench session survives a reload and an accidental back.
 *
 * ⚠️ IT IS A DEFAULT, NEVER A DECISION. It fills in only where the device
 * itself has nothing to say — an already-assigned device always shows its own
 * truth, or the screen would be lying about where a device is. And it says on
 * screen that it was carried over, because a pre-filled room that the operator
 * assumes came from the device is how thirty switches end up in the wrong one.
 */
const STICKY_KEY = 'sli.deviceAdmin.lastAssign';

const loadSticky = () => {
  try {
    const raw = window.localStorage.getItem(STICKY_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && v.userId != null ? v : null;
  } catch (e) {
    return null;
  }
};

const saveSticky = (v) => {
  try {
    window.localStorage.setItem(STICKY_KEY, JSON.stringify(v));
  } catch (e) { /* private mode — the batch just does not survive a reload */ }
};

// A dropdown you can type into.
//
// Every list on this page can be long — 450+ users, and a location with dozens
// of rooms or devices — so a plain Select means scrolling to find something you
// already know the name of. Autocomplete filters as you type and still behaves
// as a picker (no free text: the value must be one of the options).
//
// ⚠️ VALUES ARE COMPARED WITH String(), NOT ===, AND '' IS THE ONLY "UNSET".
// Room 0 is a legitimate choice (Common devices) and is falsy, so the usual
// `value || null` and `if (!value)` shortcuts would silently unselect it.
/*
 * ── One compact scale for the whole screen ─────────────────────────────────
 *
 * This page is a DENSE READOUT — eleven device facts, the access list, assign,
 * Wi-Fi, firmware and now the controls, all meant to be read at once without
 * scrolling. At MUI's default sizes the three columns push past the fold and
 * the thing it exists for (seeing the whole device at a glance) stops working.
 *
 * Applied as descendant selectors on the root rather than as a prop on each of
 * ~40 elements: it is one number to change, and a control added tomorrow
 * inherits it instead of being the one oversized thing on the page.
 *
 * ⚠️ It DOES reach the embedded control card too — a descendant selector is two
 * classes and beats the card's own one-class `sx`. That is deliberate: the card
 * should be the same scale as everything around it. Padding and hit areas are
 * untouched, so the buttons stay as pressable as they were.
 */
const COMPACT = {
  '& .MuiTypography-overline': { fontSize: 9.5, letterSpacing: 0.5, lineHeight: 1.5 },
  '& .MuiTypography-body1': { fontSize: 12 },
  '& .MuiTypography-body2': { fontSize: 11.5 },
  '& .MuiTypography-caption': { fontSize: 10 },
  '& .MuiTableCell-root': { fontSize: 11 },
  '& .MuiButton-root': { fontSize: 11 },
  '& .MuiChip-label': { fontSize: 10 },
  '& .MuiInputBase-input': { fontSize: 11.5 },
  '& .MuiFormLabel-root': { fontSize: 11 },
  '& .MuiListItemText-primary': { fontSize: 11.5 },
  '& .MuiListItemText-secondary': { fontSize: 10 },
  '& .MuiAlert-message': { fontSize: 11 },
  /*
   * ⚠️ THE FIELD HEIGHT IS WHERE THE SPACE ACTUALLY IS, not the type size.
   * A small MUI input is 40px of control plus ~20px of helper line, and this
   * panel stacks nine of them — trimming both is worth more than a point of
   * font on every label, and it is what lets Device type and the address block
   * sit on screen instead of under a fold.
   *
   * ⚠️ UNITS ARE MANDATORY ON PADDING AND MARGIN HERE. In MUI's `sx` a NUMBER
   * on a spacing property is theme spacing units, not pixels — `paddingTop: 6`
   * is `theme.spacing(6)` = 48px. Written that way this rule turned the Unique
   * Number box into a ~130px tall square. `fontSize` and `lineHeight` are not
   * spacing properties, so a bare number there is fine; padding and margin are.
   */
  '& .MuiOutlinedInput-input': { paddingTop: '6px', paddingBottom: '6px' },
  '& .MuiFormHelperText-root': { marginTop: '1px', marginLeft: '6px', fontSize: 9.5, lineHeight: 1.3 },
  '& .MuiAutocomplete-input': { paddingTop: '2px !important', paddingBottom: '2px !important' },
};

/*
 * How many matches the list draws at once.
 *
 * ⚠️ 457 USERS IS TOO MANY TO RENDER, and that is what made the picker feel
 * unsearchable rather than merely long: MUI builds every matching option as a
 * DOM node on EVERY keystroke, so the first two characters — which match almost
 * everything — repaint hundreds of two-line rows before the third character is
 * accepted. It reads as a frozen box, so people stop typing and start
 * scrolling, which is the slowest way through 457 names.
 *
 * Capping the DRAW makes typing instant. The cap is never silent: what it hides
 * is counted in a final, unselectable row, because a filter that quietly stops
 * at N is how a person you are certain exists appears not to.
 */
const PICKER_DRAW_LIMIT = 40;
const MORE_ID = '__more__';

function Picker({
  label, value, onChange, options, disabled, width, helperText, autoFocusText, grow,
}) {
  const selected = options.find((o) => String(o.id) === String(value)) ?? null;
  return (
    <Autocomplete
      size="small"
      // `grow` lets a picker share a row instead of holding a fixed slot with
      // dead space beside it — see the note on the top bar.
      sx={grow ? { flex: `1 1 ${width}px`, minWidth: 116 } : { width }}
      options={options}
      value={value === '' || value == null ? null : selected}
      onChange={(_, o) => onChange(o && o.id !== MORE_ID ? o.id : '')}
      disabled={disabled}
      getOptionLabel={(o) => o.label ?? ''}
      // Opening on focus and highlighting the top match turn this into a
      // type-and-press-Enter box instead of a click-then-scroll one.
      openOnFocus
      autoHighlight
      selectOnFocus
      handleHomeEndKeys
      getOptionDisabled={(o) => o.id === MORE_ID}
      // Word-order-independent, and searching the SUB line too — so a user is
      // found by mobile number and a device by its unique number, not just by
      // the name on the first line.
      // The "＋ New …" row survives both the search and the draw cap — see
      // pickerOptions in core/utils/search, which is where that rule and the
      // two ways it has been broken are written down.
      filterOptions={(opts, state) => pickerOptions(opts, state.inputValue, {
        textOf: (o) => `${o.label ?? ''} ${o.sub ?? ''}`,
        isCreate: (o) => o.id === NEW,
        limit: PICKER_DRAW_LIMIT,
        more: (hidden) => ({
          id: MORE_ID,
          label: `…and ${hidden} more — keep typing to narrow`,
        }),
      })}
      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
      renderOption={(props, o) => (
        <li {...props} key={String(o.id)}>
          <Box>
            <Typography variant="body2" fontWeight={600}>{o.label}</Typography>
            {o.sub && (
              <Typography variant="caption" color="text.secondary">{o.sub}</Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(p) => (
        <TextField {...p} label={label} helperText={helperText} autoFocus={autoFocusText} />
      )}
    />
  );
}

// A server timestamp, readable.
//
// ⚠️ THROUGH parseServerTime, ALWAYS. The backend writes IST wall-clock and
// labels it `…Z`, so printing the raw string — which this page used to do — is
// off by five and a half hours and reads as a device that has not been seen
// since this evening when it answered a minute ago.
//
// The relative part is the one that gets read; the absolute date is what gets
// quoted back on the phone, so both are shown.
function When({ raw }) {
  const ms = parseServerTime(raw);
  if (ms == null) return '—';
  const mins = Math.round((Date.now() - ms) / 60000);
  const rel = mins < 1 ? 'just now'
    : mins < 60 ? `${mins}m ago`
      : mins < 2880 ? `${Math.floor(mins / 60)}h ago`
        : `${Math.floor(mins / 1440)}d ago`;
  return (
    <>
      {new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
      <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.75 }}>
        {rel}
      </Box>
    </>
  );
}

// Option builders — one place, so the browse and assign sides cannot drift.
const userOptions = (users) => users.map((u) => ({
  id: u.UserId, label: userLabel(u), sub: `${u.MobileNumber || ''} · ID ${u.UserId}`, raw: u,
}));
const locationOptions = (locs) => locs.map((l) => ({
  id: l.LocationID, label: l.LocationName || `Location ${l.LocationID}`,
}));
const roomOptions = (rooms) => rooms.map((r) => ({
  id: r.RoomID, label: r.RoomName || `Room ${r.RoomID}`,
}));
const deviceOptions = (devs) => devs.map((dev) => ({
  id: dev.UniqueNumber,
  label: dev.DeviceName || 'Unnamed',
  sub: `${dev.UniqueNumber}${dev.DeviceTypeName ? ` · ${dev.DeviceTypeName}` : ''}`,
}));

export default function DeviceAdminScreen() {
  const navigate = useNavigate();
  const { isSuperAdmin, user: me, userId } = useApp();

  const [uno, setUno] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);   // { found, device, assigned }
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [newRoom, setNewRoom] = useState('');
  const [deviceName, setDeviceName] = useState('');

  // The new location's address. Kept out of `newLocation` so the name stays one
  // value — it is the only part of a location that is required.
  const [addr, setAddr] = useState({
    add1: '', add2: '', city: '', pincode: '',
  });
  const patchAddr = (p) => setAddr((s) => ({ ...s, ...p }));

  // The last successful assignment, offered to the next unassigned device.
  const [sticky, setSticky] = useState(loadSticky);
  // Whether what is on screen right now CAME from that, rather than from the
  // device. Only ever true for a device that had no placement of its own.
  const [carried, setCarried] = useState(false);

  // ── "＋ New user" ─────────────────────────────────────────────────────────
  // A device off the bench often belongs to somebody who is not in the system
  // yet, and there was no way to say so here: Location and Room could both be
  // created inline, the OWNER could not, so the whole assignment stalled on a
  // trip to another screen.
  //
  // ⚠️ `userIsNew` IS SEPARATE FROM `user`, NOT a sentinel inside it. Every
  // cascade below reads `user.UserId` to load that person's locations; a fake
  // object with a NEW id would have them fetched for a user that does not
  // exist. While this is on there are no lists to load — a new user owns
  // nothing — so the Location and Room selects offer only their New options.
  const [userIsNew, setUserIsNew] = useState(false);
  const [nu, setNu] = useState({
    surname: '', name: '', fatherHusband: '', mobileNumber: '', password: '',
  });
  const patchNu = (p) => setNu((s) => ({ ...s, ...p }));

  // ⚠️ CHECKED BEFORE SAVING, because AddNewUser is an UPSERT on MobileNumber:
  // an existing number silently overwrites that person's name and PIN. This is
  // the friendly half of the guard; createUser raises on the reply shape too.
  const mobileTaken = useMemo(() => {
    const m = nu.mobileNumber.replace(/\D/g, '');
    if (m.length !== 10) return null;
    return users.find((u) => String(u.MobileNumber || '').replace(/\D/g, '') === m) || null;
  }, [nu.mobileNumber, users]);

  // ── Browse panel: user -> location -> room -> device ──────────────────────
  // Kept separate from the ASSIGN selects below. They look alike but mean
  // opposite things: browsing asks "where is this device now", assigning says
  // "where should it go". Sharing state would make picking a device to inspect
  // silently rewrite the destination.
  const [bUser, setBUser] = useState(null);
  const [bLocations, setBLocations] = useState([]);
  const [bLocationId, setBLocationId] = useState('');
  const [bRooms, setBRooms] = useState([]);
  const [bRoomId, setBRoomId] = useState('');
  const [bDevices, setBDevices] = useState([]);
  // The Device select used to be write-only (`value=""`): a trigger, not a
  // selection. Now that it defaults to the first device it has to SHOW which
  // one, or the page fills with a device the row does not name.
  const [bDeviceUno, setBDeviceUno] = useState('');

  /*
   * ── FIND fills the browse pickers ──────────────────────────────────────────
   *
   * The four pickers along the top and the Unique Number box are two ways into
   * the same device, and they used to ignore each other: type a number, press
   * FIND, and the pickers stayed empty — so the screen knew the device was in
   * Sanjay's Factory / Office and still showed you nothing about where that is.
   * Anyone wanting the neighbouring device had to rebuild the path by hand.
   *
   * So a find that lands on an ASSIGNED device now walks the pickers to it.
   *
   * ⚠️ IT CANNOT JUST SET THE FOUR VALUES. Each level's options are FETCHED
   * when the level above it changes, and each effect defaults to the first row
   * of what came back — a default that would immediately overwrite whatever was
   * set for it, and, at the device level, run a fresh lookup on the wrong
   * device. So the target is parked in a ref and each level PREFERS it over the
   * first row, exactly like `preselect` does for the assign panel.
   *
   * Cleared once the device level has consumed it, so a later manual pick is
   * never dragged back to the found device.
   */
  const browseWant = useRef(null);   // { userId, locationId, roomId, uno }
  // Finding a device owned by the user the pickers are ALREADY on leaves
  // `setBUser` a no-op, so nothing would re-run. This is what makes the cascade
  // look again — the same trick `preselect` plays on the assign side.
  const [browseNudge, setBrowseNudge] = useState(0);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [deviceType, setDeviceType] = useState('');

  // Unassign is destructive and irreversible from here, so it asks for the
  // signed-in admin's own password — the same guard the old form's operators
  // expect, and enough to stop a mis-click emptying a live installation.
  const [showMore, setShowMore] = useState(false);
  const [unassignOpen, setUnassignOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState('');

  // What the device already has, held until its list has loaded. The cascades
  // below CLEAR the level under them whenever their parent changes, so a
  // preselect written straight after search() is wiped a tick later — it has to
  // be applied when the options actually arrive.
  const pending = useRef(null);   // { locationId, roomId }

  // Latest pickDevice, for the browse cascade to call. Declared here because
  // the effect that uses it is written above the function it holds.
  const pickRef = useRef(() => {});

  // ⚠️ THE CASCADE MUST RE-RUN ON EVERY LOOKUP, INCLUDING ONE FOR THE SAME OWNER.
  //
  // `setUser(users.find(...))` hands React the SAME object it is already holding
  // when two devices share a master user, so the state does not change, the two
  // effects below never fire, and Location / Room keep the PREVIOUS device's
  // answer while the facts table beside them shows this one's — the same screen
  // stating two different rooms. It is not a display fault either: Save writes
  // whatever those selects hold, so the stale pair would have been written back
  // and the device actually moved.
  //
  // This counter is bumped by every successful lookup and is a dependency of
  // both cascades, so they always re-run and always re-apply `pending`.
  const [preselect, setPreselect] = useState(0);

  // Set when the device's own location or room is NOT in the owner's list, so a
  // blank select is explained rather than read as "this device has no room".
  const [preselectMiss, setPreselectMiss] = useState('');   // '' | 'location' | 'room'

  // Users who can see this device.
  // ── Firmware, independent of the assignment ─────────────────────────────
  // Its preconditions are different (no owner, no location needed) and so is
  // its table, so it has its own state and its own save.
  const [bins, setBins] = useState([]);
  const [binPick, setBinPick] = useState('');
  const [servedNow, setServedNow] = useState('');
  const [pendingCmd, setPendingCmd] = useState('');
  const [fwBusy, setFwBusy] = useState(false);
  const [fwMsg, setFwMsg] = useState(null);      // { severity, text }
  const [otaLines, setOtaLines] = useState([]);
  const otaStop = useRef(null);

  useEffect(() => {
    getOtaBinFiles()
      // Row 17 carries an empty BinFileName; as an option it reads as blank and
      // would un-point the device.
      .then((res) => setBins((Array.isArray(res.data) ? res.data : [])
        .filter((r) => String(r.BinFileName || '').trim())))
      .catch(() => setBins([]));
  }, []);

  // Stop listening when the page goes away — the OTA watch holds a subscription.
  useEffect(() => () => { if (otaStop.current) otaStop.current(); }, []);

  const [access, setAccess] = useState([]);      // UserId[]
  const [addUserId, setAddUserId] = useState('');
  const [accessBusy, setAccessBusy] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    getDeviceTypes()
      .then((res) => setTypes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTypes([]));
    getAllUsers()
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUsers([]));
  }, [isSuperAdmin]);

  // Takes the number EXPLICITLY when called from the browse panel: setUno()
  // does not update the value this closure captured, so searching "the current
  // box" straight after selecting a device would look up the previous one.
  //
  // ⚠️ `{ refresh: true }` FOR THE RE-READ AFTER AN ACTION, AND IT IS NOT
  // COSMETIC. Every action here ends `setSaved(…)` / `setFwMsg(…)` and then
  // `await search()` to pick the new row up — and search's own reset then wiped
  // the message the action had just written, one tick later. "Assigned to X",
  // "Confirmed — the device will download …" and "Parked …" were all being
  // cleared by the very refresh meant to confirm them, so the operator saw a
  // button do nothing. A refresh also keeps `result` and the OTA log, because
  // blanking the panel it is re-reading is a flicker with no purpose.
  const search = useCallback(async (value, opts) => {
    const target = String(value ?? uno).trim();
    if (!target) return;
    const refresh = !!(opts && opts.refresh);
    setSearching(true);
    if (!refresh) {
      setError('');
      setSaved('');
      setResult(null);
    }
    try {
      const r = await findDevice(target);
      setResult(r);
      if (r.found) {
        setDeviceName(r.device.DeviceName || '');
        setDeviceType(r.device.DeviceID ?? '');

        /*
         * Walk the browse pickers to this device — but ONLY when it is assigned
         * and only on a real find. An unassigned device has no owner, location
         * or room to point at, and a `refresh` is a re-read of the device the
         * pickers are already on, so re-driving them would fight a manual pick
         * the operator made in between.
         */
        const ownerId = r.device.MasterUserId ?? r.device.DeviceMasterUserId;
        if (!refresh && r.assigned && ownerId != null) {
          browseWant.current = {
            userId: ownerId,
            locationId: r.device.LocationID ?? r.device.LocationId ?? '',
            roomId: r.device.RoomId ?? '',
            uno: String(r.device.UniqueNumber),
          };
          const bOwner = users.find((u) => String(u.UserId) === String(ownerId));
          // Same owner is a no-op for the effect, so the cascade below has to be
          // able to re-run from the ref alone — hence `browseNudge`.
          if (bOwner) setBUser(bOwner);
          setBrowseNudge((n) => n + 1);
        }
        // Start the edit from the TRUTH, not from blank: the panel should show
        // where the device is now so a change is a change, not a re-entry.
        pending.current = {
          locationId: r.device.LocationID ?? r.device.LocationId ?? '',
          roomId: r.device.RoomId ?? '',
        };
        const owner = users.find((u) => String(u.UserId) === String(r.device.MasterUserId));

        /*
         * ── Carrying the batch forward ────────────────────────────────────
         *
         * A device with no owner and no location has nothing of its own to
         * show, so the last assignment is offered instead — that is the
         * thirty-switches-into-one-flat case.
         *
         * ⚠️ ONLY WHEN THE DEVICE IS TRULY UNPLACED. `r.assigned` is
         * LocationID > 0, and `owner` is null for the factory's MasterUserId
         * of -2. An assigned device always shows its own placement; overriding
         * that with a remembered one would make the panel disagree with the
         * facts table beside it, and a Save would then MOVE a live device.
         */
        const stickyFits = !r.assigned && !owner && sticky && sticky.userId != null;
        if (stickyFits) {
          const carriedUser = users.find((u) => String(u.UserId) === String(sticky.userId));
          if (carriedUser) {
            pending.current = { locationId: sticky.locationId, roomId: sticky.roomId };
            setUser(carriedUser);
            setCarried(true);
          } else {
            // The remembered owner has since been deleted — drop the memory
            // rather than silently assigning to nobody.
            setSticky(null);
            setCarried(false);
            setUser(null);
          }
        } else {
          setCarried(false);
          setUser(owner || null);
        }
        // ⚠️ AFTER setUser AND UNCONDITIONALLY — see the note on `preselect`.
        // Same owner means setUser is a no-op, and this is then the only thing
        // that makes the Location / Room cascade look at the new device.
        setPreselect((n) => n + 1);
        deviceUserIds(target).then(setAccess).catch(() => setAccess([]));
        // Firmware panel starts from the truth too.
        setBinPick(r.device.OTABinFileId != null ? String(r.device.OTABinFileId) : '');
        setPendingCmd(r.device.PendingCommand || '');
        if (!refresh) { setFwMsg(null); setOtaLines([]); }
        servedOtaUrl(target).then(setServedNow).catch(() => setServedNow(''));
      } else {
        setAccess([]);
        setServedNow('');
      }
    } catch (e) {
      setError('Lookup failed. Check the connection and try again.');
    } finally {
      setSearching(false);
    }
  }, [uno, users, sticky]);

  // Locations follow the chosen user — and the lookup, so that a second device
  // with the SAME owner still re-applies the preselect. See `preselect`.
  //
  // ⚠️ THE `alive` GUARD IS LOAD-BEARING. Two lookups in a row leave the first
  // list in flight; without it, the slower reply lands last and preselects the
  // location of a device that is no longer on screen.
  useEffect(() => {
    setLocationId('');
    setRooms([]);
    setRoomId('');
    setPreselectMiss('');
    if (!user) { setLocations([]); pending.current = null; return undefined; }
    let alive = true;
    locationsOf(user.UserId).then((list) => {
      if (!alive) return;
      setLocations(list);
      // The device's own location, once its list exists.
      const want = pending.current?.locationId;
      const wanted = want !== undefined && want !== '' && Number(want) > 0;
      if (wanted && list.some((l) => String(l.LocationID) === String(want))) {
        setLocationId(want);
        return;
      }
      // Nothing to hang a room preselect on, and the ref must not survive into a
      // later MANUAL location pick — it would preselect a room belonging to a
      // device the operator has stopped looking at.
      if (wanted) setPreselectMiss('location');
      pending.current = null;
    }).catch(() => {
      if (!alive) return;
      setLocations([]);
      pending.current = null;
    });
    return () => { alive = false; };
  }, [user, preselect]);

  // Rooms follow the chosen location — and the lookup, for the same reason.
  useEffect(() => {
    setPreselectMiss('');
    /*
     * ⚠️ A NEW LOCATION HAS NO ROOMS, AND THAT USED TO DEADLOCK THE SAVE.
     * `canSave` requires a room, this effect blanks it whenever the parent
     * changes, and there is no list to pick from — so choosing "＋ New
     * location…" left Room empty, Save greyed out, and nothing on screen saying
     * which field was missing. That is the reported "Save is not allowing me to
     * save".
     *
     * Common device (RoomId 0) is the honest default: it is what a device that
     * belongs to the location rather than to any room IS, it is the only choice
     * that exists before a room does, and "＋ New room…" is still one click
     * away. See COMMON_ROOM_ID in core/devices/deviceAdmin.
     */
    if (!user && !userIsNew) { setRoomId(''); setRooms([]); return undefined; }
    if (!locationId || locationId === NEW) {
      setRooms([]);
      setRoomId(locationId === NEW ? COMMON_ROOM_ID : '');
      return undefined;
    }
    setRoomId('');
    let alive = true;
    roomsOf(user.UserId, locationId).then((list) => {
      if (!alive) return;
      setRooms(list);
      const want = pending.current?.roomId;
      // Room 0 is valid (common device), so this tests for undefined, not truth.
      if (want !== undefined && want !== '') {
        if (Number(want) === COMMON_ROOM_ID
          || list.some((r) => String(r.RoomID) === String(want))) {
          setRoomId(Number(want));
        } else {
          setPreselectMiss('room');
        }
      }
      pending.current = null;   // one-shot: never fight a later manual change
    }).catch(() => { if (alive) setRooms([]); });
    return () => { alive = false; };
  }, [user, userIsNew, locationId, preselect]);

  // ── Browse cascade ────────────────────────────────────────────────────────
  // Each level clears the ones below it, so a stale room from the previous
  // location can never be shown as belonging to the new one — and then each
  // level DEFAULTS to the first of its own list, so choosing a user selects
  // straight through to a device instead of stopping three clicks short.
  //
  // ⚠️ THE DEVICE DEFAULT RUNS THE LOOKUP. In this row picking a device IS the
  // lookup, so a user chosen here fills the whole page. That is the intent, and
  // it is also why every level below is guarded by `alive`: a second user picked
  // while the first user's lists are still in flight would otherwise land last
  // and open a device belonging to the user no longer selected.
  useEffect(() => {
    setBLocationId(''); setBRooms([]); setBRoomId(''); setBDevices([]);
    if (!bUser) { setBLocations([]); return undefined; }
    let alive = true;
    locationsOf(bUser.UserId).then((list) => {
      if (!alive) return;
      setBLocations(list);
      if (!list.length) return;
      // The found device's location if it is one of this user's, else the first.
      const want = browseWant.current?.locationId;
      const hit = want != null && list.find((l) => String(l.LocationID) === String(want));
      setBLocationId(hit ? hit.LocationID : list[0].LocationID);
    }).catch(() => { if (alive) setBLocations([]); });
    return () => { alive = false; };
  }, [bUser, browseNudge]);

  useEffect(() => {
    setBRoomId(''); setBDevices([]);
    if (!bUser || !bLocationId) { setBRooms([]); return undefined; }
    let alive = true;
    roomsOf(bUser.UserId, bLocationId).then((list) => {
      if (!alive) return;
      setBRooms(list);
      // The found device's room wins when it is in this location's list. A
      // roomless device (RoomId 0) is a COMMON device, and that is a real
      // answer, so it is honoured rather than treated as "no preference".
      const want = browseWant.current?.roomId;
      if (want != null && want !== '') {
        if (Number(want) === COMMON_ROOM_ID) { setBRoomId(COMMON_ROOM_ID); return; }
        const hit = list.find((r) => String(r.RoomID) === String(want));
        if (hit) { setBRoomId(hit.RoomID); return; }
      }
      // The first REAL room. "Common devices" leads the picker's option list but
      // is not one of the location's rooms — it is the RoomId 0 bucket, and
      // defaulting to it would usually default to an empty one. It is the
      // fallback only when the location genuinely has no rooms.
      setBRoomId(list.length ? list[0].RoomID : COMMON_ROOM_ID);
    }).catch(() => { if (alive) setBRooms([]); });
    return () => { alive = false; };
  }, [bUser, bLocationId, browseNudge]);

  useEffect(() => {
    setBDeviceUno('');
    if (!bUser || !bLocationId || bRoomId === '') { setBDevices([]); return undefined; }
    let alive = true;
    devicesIn(bUser.UserId, bLocationId, bRoomId).then((list) => {
      if (!alive) return;
      setBDevices(list);

      /*
       * ⚠️ THE FOUND DEVICE IS SELECTED WITHOUT RE-RUNNING THE LOOKUP. We are
       * here BECAUSE it was just looked up; calling pickRef would fire a second
       * identical search and, worse, wipe the panel state that find had only
       * just filled in. Setting the value is the whole job.
       *
       * This is also where the target is consumed — from here on the pickers
       * behave normally, so a manual pick is never dragged back.
       */
      const want = browseWant.current;
      if (want) {
        browseWant.current = null;
        if (list.some((x) => String(x.UniqueNumber) === want.uno)) {
          setBDeviceUno(want.uno);
          return;
        }
        // The device is not in the list its own row points at — a stale mapping.
        // Leave the picker empty rather than opening a neighbour and implying
        // it is the device that was searched for.
        setBDeviceUno('');
        return;
      }

      // Not a highlight — this is the lookup. See the note above.
      if (list.length) pickRef.current(list[0].UniqueNumber);
    }).catch(() => { if (alive) setBDevices([]); });
    return () => { alive = false; };
  }, [bUser, bLocationId, bRoomId, browseNudge]);

  // Picking a device fills the box AND runs the lookup, passing the number
  // explicitly — see the note on search().
  const pickDevice = (u) => {
    if (!u) { setBDeviceUno(''); return; }
    setBDeviceUno(String(u));
    setUno(String(u));
    search(String(u));
  };

  // ⚠️ A REF, NOT A DEPENDENCY OF THE DEVICES EFFECT ABOVE. pickDevice writes
  // the number into the search box, which re-creates search() and so
  // pickDevice; as a dependency, its own auto-pick would re-trigger the effect
  // that called it, re-fetch the room and pick again.
  useEffect(() => { pickRef.current = pickDevice; });

  /*
   * Why Save is unavailable, in words.
   *
   * ⚠️ THIS IS THE HALF THAT WAS MISSING. `canSave` is a chain of six
   * conditions and the button only ever showed the RESULT, so a panel that
   * looked complete but had one blank select read as a broken button rather
   * than a missing field. It is the tooltip on the disabled icon.
   */
  const whyCannotSave = useMemo(() => {
    if (!result?.found) return 'Find a device first';
    if (userIsNew) {
      if (!nu.name.trim() && !nu.surname.trim()) return 'The new user needs a name';
      if (nu.mobileNumber.replace(/\D/g, '').length !== 10) return 'The new user needs a 10-digit mobile number';
      if (mobileTaken) return `${nu.mobileNumber} already belongs to ${userLabel(mobileTaken)}`;
      if (!nu.password.trim()) return 'The new user needs a PIN';
    } else if (!user) return 'Choose a master user';
    if (!locationId) return 'Choose a location';
    if (locationId === NEW && !newLocation.trim()) return 'Type the new location’s name';
    if (roomId === '') return 'Choose a room, or Common device';
    if (roomId === NEW && !newRoom.trim()) return 'Type the new room’s name';
    return '';
  }, [result, user, userIsNew, nu, mobileTaken, locationId, newLocation, roomId, newRoom]);

  /*
   * Does the panel differ from what is actually stored on the device?
   *
   * ⚠️ COMPARED AGAINST THE DEVICE ROW, NOT A SNAPSHOT TAKEN AT LOAD. A
   * snapshot has to be re-taken on every refresh and goes stale the one time
   * that is missed; the row IS the saved state, so this cannot drift from it.
   *
   * An UNASSIGNED device always reads as dirty, and that is correct rather than
   * a quirk: nothing about it is stored yet, so there is always something to
   * save — even before a field is touched. `canSave` is what decides whether
   * the button can be pressed; this only decides whether it is offered.
   */
  const assignDirty = useMemo(() => {
    // ⚠️ `result.device`, not the `d` alias — that is declared below this, and
    // a useMemo body runs during the same render.
    const row = result?.found ? result.device : null;
    if (!row) return false;
    // Creating anything is, by definition, not what is stored.
    if (userIsNew || locationId === NEW || roomId === NEW) return true;
    const same = (a, b) => String(a ?? '') === String(b ?? '');
    if (!same(deviceName, row.DeviceName)) return true;
    if (!same(user ? user.UserId : '', row.MasterUserId)) return true;
    if (!same(locationId, row.LocationID ?? row.LocationId)) return true;
    if (!same(roomId, row.RoomId)) return true;
    if (!same(deviceType, row.DeviceID)) return true;
    return false;
  }, [result, userIsNew, locationId, roomId, deviceName, user, deviceType]);

  const canSave = useMemo(() => {
    if (!result?.found) return false;
    if (userIsNew) {
      // A new owner needs enough to log in with: a name, a 10-digit mobile and
      // the 4-digit PIN. And the number must not already be somebody.
      if (!nu.name.trim() && !nu.surname.trim()) return false;
      if (nu.mobileNumber.replace(/\D/g, '').length !== 10) return false;
      if (!nu.password.trim()) return false;
      if (mobileTaken) return false;
    } else if (!user) return false;
    if (!locationId) return false;
    if (locationId === NEW && !newLocation.trim()) return false;
    if (roomId === '') return false;
    if (roomId === NEW && !newRoom.trim()) return false;
    return true;
  }, [result, user, userIsNew, nu, mobileTaken, locationId, newLocation, roomId, newRoom]);

  const save = async () => {
    setSaving(true);
    setError('');
    setSaved('');
    try {
      /*
       * ⚠️ THE OWNER IS CREATED FIRST, AND EVERYTHING ELSE HANGS OFF ITS ID.
       * A location's `MasterUserId` and both mappings need a real UserId, so
       * the order is owner → location → room → device and cannot be rearranged.
       * `me.CompanyInfoType` is inherited deliberately: it decides the theme and
       * which company the account belongs to, and a null there gives the new
       * customer a blank-branded app.
       */
      let ownerId = user ? user.UserId : null;
      let ownerName = user ? userLabel(user) : '';
      if (userIsNew) {
        ownerId = await createUser({ ...nu, companyInfoType: me?.CompanyInfoType });
        ownerName = [nu.surname, nu.name, nu.fatherHusband].filter(Boolean).join(' ').trim();
      }

      let locId = locationId;
      if (locationId === NEW) locId = await createLocation(ownerId, newLocation.trim(), addr);

      let rmId = roomId;
      if (roomId === NEW) rmId = await createRoom(ownerId, locId, newRoom.trim());

      await assignDevice({
        device: result.device,
        uniqueNumber: uno.trim(),
        userId: ownerId,
        locationId: locId,
        roomId: rmId,
        deviceName,
      });
      // Type lives on the same row but is not part of the assignment chain, so
      // it is written separately and only when it actually changed.
      // ⚠️ saveDeviceType, NOT setDeviceType — the state setter of the same
      // name shadows the import, so calling it here would silently set local
      // state and never reach the server.
      if (deviceType !== '' && String(deviceType) !== String(result.device.DeviceID)) {
        await saveDeviceType(uno.trim(), deviceType);
      }
      // Remember where this one went, for the next unique number typed in.
      const remember = { userId: ownerId, locationId: locId, roomId: rmId };
      setSticky(remember);
      saveSticky(remember);
      setCarried(false);

      setSaved(`Assigned to ${ownerName}${userIsNew ? ' (new user created)' : ''}.`);
      // ⚠️ The address belongs to the location that was just created. Left in
      // place it would be pre-filled for the NEXT device looked up — and if that
      // one also needed a new location, this customer's address would be written
      // onto somebody else's site without anybody retyping it.
      setAddr({
        add1: '', add2: '', city: '', pincode: '',
      });
      if (userIsNew) {
        // The picker is fed from this list, so without a re-read the person we
        // just created cannot be selected again — including by the refresh
        // below, which looks the owner up by id.
        setUserIsNew(false);
        setNu({
          surname: '', name: '', fatherHusband: '', mobileNumber: '', password: '',
        });
        await getAllUsers()
          .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
          .catch(() => {});
      }
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setError(e.message || 'Could not save the assignment.');
    } finally {
      setSaving(false);
    }
  };

  /*
   * ── Ctrl+S and Ctrl+Shift+Enter save the assignment ───────────────────────
   *
   * Commissioning a box of switches is: type a number, Enter, adjust a field,
   * save — over and over. Reaching for a small icon in a panel header on every
   * one of those is the slowest part of the loop, and the whole point of the
   * batch carry-over is that most devices need no adjustment at all.
   *
   * ⚠️ `preventDefault` ON Ctrl+S EVEN WHEN WE CANNOT SAVE. The browser's own
   * "Save page as…" dialog is bound to it, and letting that through would drop
   * a file picker over the app mid-batch. Swallowing it unconditionally on this
   * screen is the point — the shortcut has to be reliable, not conditional.
   *
   * ⚠️ A DIALOG OWNS THE KEYBOARD WHILE IT IS OPEN. Rename-room and the
   * password-guarded Unassign both sit over this panel; saving the assignment
   * behind one of them would be an invisible write the operator never asked
   * for.
   *
   * The handler lives in a ref so the listener is bound once. Re-subscribing on
   * every render works too, but this screen re-renders on every keystroke in
   * seven text fields.
   */
  /*
   * ── Escape: clear for the next number, then leave ─────────────────────────
   *
   * The other half of the batch loop. Type a number, Enter, Ctrl+S, Escape —
   * and the box is empty and focused for the next one, without touching the
   * mouse.
   *
   * ⚠️ IT DISCARDS ONLY LOCAL FORM STATE. Nothing on the server changes, so
   * "cancel" is the ordinary meaning of the key here; there is no half-written
   * row to lose. Unsaved EDITS do go, which is what Escape means everywhere
   * else, and the ✓/Save icon says which state the panel was in.
   *
   * ⚠️ MUI EATS THE KEY FIRST WHERE IT SHOULD. An open Autocomplete and an open
   * Dialog both call stopPropagation on Escape, so this never fires while a
   * dropdown is showing or a dialog is up — closing those is what the operator
   * meant. The explicit dialog guard below is belt and braces.
   */
  const unoRef = useRef(null);
  const escapeHotkey = useRef(() => {});
  useEffect(() => {
    escapeHotkey.current = () => {
      if (renaming || unassignOpen) return;
      // Something on screen -> clear it and wait for the next number.
      if (uno.trim() || result) {
        setUno('');
        setResult(null);
        setBDeviceUno('');
        setError('');
        setSaved('');
        setUserIsNew(false);
        setCarried(false);
        setFwMsg(null);
        setOtaLines([]);
        setServedNow('');
        setAccess([]);
        if (unoRef.current) unoRef.current.focus();
        return;
      }
      // Already empty — the second press. Back where they came from.
      navigate(-1);
    };
  });

  const saveHotkey = useRef(() => {});
  useEffect(() => {
    saveHotkey.current = () => {
      if (renaming || unassignOpen) return;
      // Nothing differs from the stored row — the same guard the button uses,
      // so a stray Ctrl+S cannot re-write identical values.
      if (!assignDirty) return;
      if (!canSave || saving) return;
      save();
    };
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { escapeHotkey.current(); return; }
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      const isS = e.key === 's' || e.key === 'S';
      const isShiftEnter = e.shiftKey && e.key === 'Enter';
      if (!isS && !isShiftEnter) return;
      e.preventDefault();
      saveHotkey.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Firmware actions ─────────────────────────────────────────────────────

  // Write DevicesInstalled.OTABinFileId, then ask the firmware's own endpoint
  // what the device will now download. The read-back is the point: everything
  // in this area fails by reporting success.
  const saveBin = async () => {
    setFwBusy(true); setFwMsg(null);
    try {
      const row = bins.find((b) => String(b.OTABinFileId) === String(binPick));
      const out = await mapDeviceToBinFile(uno.trim(), binPick, row && row.BinFileName);
      setServedNow(out.url);
      setFwMsg(out.ok
        ? { severity: 'success', text: `Confirmed — the device will download ${out.url}` }
        : {
          severity: 'warning',
          text: `Saved, but the server still resolves this device to ${out.url || '(nothing)'}.`
            + ' Do not send Update code until that is explained.',
        });
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setFwMsg({ severity: 'error', text: 'Could not set the firmware file.' });
    } finally {
      setFwBusy(false);
    }
  };

  // ── The silent-failure fallback ──────────────────────────────────────────
  // A publish to a device that is not connected is DROPPED, and MQTT cannot
  // report that: publishing to nobody is not an error. So the button used to
  // succeed loudly and do nothing, and the operator had to notice the absence
  // of a reply and type UPDATE CODE into the pending box themselves.
  //
  // Silence for OTA_SILENCE_MS is the missing signal. When it comes, the MQTT
  // route has definitively failed, and the poll queue is the only route left.
  const parked = useRef(false);

  const parkAfterSilence = async () => {
    const target = uno.trim();
    const already = result?.device?.PendingCommand;
    const secs = Math.round(OTA_SILENCE_MS / 1000);

    // ⚠️ ONE SLOT, NOT A QUEUE. Overwriting somebody's RESTART with our own
    // command, automatically, because a different device did not answer, is a
    // silent destructive write. Repeating an update is free; replacing an
    // instruction is not.
    if (already && !isUpdateCommand(already)) {
      setFwMsg({
        severity: 'warning',
        text: `No answer in ${secs}s — the device is not listening, so nothing was sent.`
          + ` NOT queued either: “${already}” is already parked and the slot holds only one`
          + ' command. Clear it below if the update should take its place.',
      });
      return;
    }
    try {
      // Both routes, because this is the case they exist for: the publish went
      // into a device that is not connected, so MQTT has definitively failed and
      // only the server-side ones are left.
      await markFirmwareUpdate(target);
      parked.current = true;
      setPendingCmd(PENDING_UPDATE_COMMAND);
      setFwMsg({
        severity: 'warning',
        text: `No answer in ${secs}s, so ${PENDING_UPDATE_COMMAND} was parked for the device's`
          + ` next poll — about 20 minutes — and ${UPDATE_MARK_COLUMN} was set, which it also`
          + ' reads on its next power-up and which stays set until a flash succeeds.'
          + ' Nothing acknowledges either, so confirm with “What is it running?” later'
          + ' rather than treating this as done.',
      });
      await search(target, { refresh: true });
    } catch (e) {
      setFwMsg({
        severity: 'error',
        text: `No answer in ${secs}s, and the update could not be queued either.`
          + ' The device has not been told anything — try again when it is back.',
      });
    }
  };

  // ⚠️ A LATE ANSWER MEANS THE PREMISE WAS WRONG. The queue is for a device
  // that is not listening; one that speaks at twelve seconds was listening all
  // along and is updating right now, so leaving the command parked re-flashes it
  // again twenty minutes later for nothing. Withdraw it — and only ever what we
  // ourselves parked, never something that was already there.
  const unpark = async () => {
    parked.current = false;
    try {
      // Both of them: `parkAfterSilence` set both, so withdrawing only the
      // parked command would leave the mark to re-flash it on its next reboot.
      await clearFirmwareUpdateMark(uno.trim(), PENDING_UPDATE_COMMAND);
      setPendingCmd('');
      setFwMsg({
        severity: 'info',
        text: 'The device answered after all, so the queued update and the update mark'
          + ' were both withdrawn — it is updating now and does not need to do it again.',
      });
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setFwMsg({
        severity: 'warning',
        text: 'The device answered after all, but the queued update could not be withdrawn.'
          + ' Clear the pending message below, or it will update again on its next poll.',
      });
    }
  };

  // Publish UPDATE CODE and watch what the device says back. Reaches a device
  // that is listening NOW; the pending message below is for one that is not.
  const sendUpdate = () => {
    if (otaStop.current) otaStop.current();
    setOtaLines([]);
    parked.current = false;
    setFwMsg({ severity: 'info', text: `Published ${otaPayload(uno.trim(), userId)} — watching ${uno.trim()}/client.` });
    otaStop.current = startOtaAndWatch(uno.trim(), userId, (ev) => {
      if (parked.current) unpark();
      setOtaLines((prev) => {
        const next = [...prev, ev];
        const out = otaOutcome(next);
        if (out === 'failed') {
          setFwMsg({
            severity: 'error',
            text: 'The device refused or could not complete it. A repeated'
              + ' "OTA FAILED (download)" usually means the image is for a different chip.',
          });
        } else if (out === 'flashed' || out === 'back') {
          // ⚠️ Not success: a crash-looping image is reverted about a minute
          // after boot, having already published OTA OK.
          setFwMsg({
            severity: 'warning',
            text: 'It flashed and rebooted — but that is not proof the release changed.'
              + ' An image that crash-loops is reverted about a minute after boot,'
              + ' having already reported success. Re-check once it is back.',
          });
        }
        return next;
      });
    }, parkAfterSilence);
  };

  // ⚠️ THE ONLY CHECK THAT SETTLES IT. Comparing a version before and after
  // cannot separate a silent rollback from a device that was already on the
  // build you just flashed — observed on E072A14582D0, which reported the same
  // sha on both sides of a real update. Comparing against the FILE's sha does.
  const checkRunning = async () => {
    setFwBusy(true);
    try {
      const v = await askVersion(uno.trim(), userId);
      const row = bins.find((b) => String(b.OTABinFileId) === String(binPick));
      const want = row ? knownBuildSha(row.BinFileName) : '';
      const hit = matchesBuild(v, want);
      if (!v) {
        setFwMsg({
          severity: 'info',
          text: 'No answer. The device may be offline, or an older ESP8266 build,'
            + ' which has no VERSION command — this is not evidence of a fault.',
        });
      } else if (hit === true) {
        setFwMsg({ severity: 'success', text: `Confirmed running ${row.BinFileName} — FW ${v.version}, sha ${v.sha}.` });
      } else if (hit === false) {
        setFwMsg({
          severity: 'error',
          text: `It reports sha ${v.sha}, but ${row.BinFileName} is ${want}.`
            + ' The device is NOT running that file — either it never took, or it rolled back.',
        });
      } else {
        setFwMsg({
          severity: 'info',
          text: `Running FW ${v.version}, sha ${v.sha}, built ${v.built}.`
            + ' No published build recorded on this computer for that file, so it cannot be compared.',
        });
      }
    } catch (e) {
      setFwMsg({ severity: 'error', text: 'Could not ask for the version.' });
    } finally {
      setFwBusy(false);
    }
  };

  /*
   * One click for the command everybody actually parks here.
   *
   * The field stays for anything else — RESTART, STATUS, ALLOFF all work through
   * the same slot — but the update is the common case and the one where a typo
   * is worst: an unrecognised command sits in the slot doing nothing, silently,
   * at both ends.
   *
   * It fills the field as well as writing it, so the screen shows what was
   * parked rather than leaving the box saying something else.
   */
  const markUpdateCode = async () => {
    setFwBusy(true); setFwMsg(null);
    try {
      // BOTH server-side routes, one write. The parked command is picked up on
      // the ~20 minute poll and then cleared whether or not the flash worked;
      // the mark survives until one does. See firmwareUpdate.js.
      await markFirmwareUpdate(uno.trim());
      setPendingCmd(PENDING_UPDATE_COMMAND);
      setFwMsg({
        severity: 'success',
        text: `Marked for update. It runs "${PENDING_UPDATE_COMMAND}" on its next poll —`
          + ` within about 20 minutes — and ${UPDATE_MARK_COLUMN} is set, so it also`
          + ' updates on its next power-up and keeps trying until a flash succeeds.'
          + ' It does not need to be online now. Nothing confirms it was picked up.',
      });
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setFwMsg({ severity: 'error', text: 'Could not mark this device for update.' });
    } finally {
      setFwBusy(false);
    }
  };

  /*
   * Take the instruction back off.
   *
   * ⚠️ THE MARK DOES NOT EXPIRE. A device that can never accept its image —
   * wrong chip, missing file — re-attempts on every single Wi-Fi reconnect for
   * as long as the column reads 1, and the only thing that stops it is this.
   * The device clears it itself after a SUCCESSFUL flash, so a mark still
   * standing on a device that keeps failing is the case this exists for.
   */
  const clearUpdateMark = async () => {
    setFwBusy(true); setFwMsg(null);
    try {
      const parked = result?.device?.PendingCommand;
      await clearFirmwareUpdateMark(uno.trim(), parked);
      if (isUpdateCommand(parked)) setPendingCmd('');
      setFwMsg({
        severity: 'success',
        // Say what was LEFT alone as well as what was removed — the slot is one
        // deep and somebody else's RESTART is not ours to discard.
        text: `${UPDATE_MARK_COLUMN} cleared — it will not update itself on reconnect any more.`
          + (parked && !isUpdateCommand(parked)
            ? ` The parked command “${parked}” was left alone.`
            : ' The parked update was withdrawn too.'),
      });
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setFwMsg({ severity: 'error', text: 'Could not clear the update mark.' });
    } finally {
      setFwBusy(false);
    }
  };

  const savePending = async () => {
    setFwBusy(true); setFwMsg(null);
    try {
      await setPendingCommand(uno.trim(), pendingCmd);
      setFwMsg(pendingCmd.trim()
        ? {
          severity: 'success',
          text: `Parked "${pendingCmd.trim()}". The device runs it on its next poll,`
            + ' within about 20 minutes. Nothing confirms it was picked up.',
        }
        : { severity: 'success', text: 'Pending message cleared.' });
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setFwMsg({ severity: 'error', text: 'Could not save the pending message.' });
    } finally {
      setFwBusy(false);
    }
  };

  // Rename the selected room in place. Rooms are GLOBAL — the new name is what
  // everyone with access to that room sees, not just this user — so it asks
  // rather than editing inline.
  const [renaming, setRenaming] = useState(false);
  const [roomNewName, setRoomNewName] = useState('');
  const selectedRoom = rooms.find((r) => String(r.RoomID) === String(roomId));

  const doRenameRoom = async () => {
    const name = roomNewName.trim();
    if (!selectedRoom || !name) return;
    setSaving(true);
    try {
      await renameRoom(selectedRoom.RoomID, name);
      setRooms((prev) => prev.map((r) => (String(r.RoomID) === String(selectedRoom.RoomID)
        ? { ...r, RoomName: name } : r)));
      setRenaming(false);
      setSaved(`Room renamed to “${name}”.`);
    } catch (e) {
      setError('Could not rename the room.');
    } finally {
      setSaving(false);
    }
  };

  const refreshAccess = () => deviceUserIds(uno.trim()).then(setAccess).catch(() => {});

  const grant = async (id) => {
    if (!id) return;
    setAccessBusy(true);
    setError('');
    try {
      await grantUserAccess(result.device, id);
      setAddUserId('');
      await refreshAccess();
    } catch (e) {
      setError(e.message || 'Could not give that user access.');
    } finally {
      setAccessBusy(false);
    }
  };

  const revoke = async (id) => {
    setAccessBusy(true);
    setError('');
    try {
      await revokeUserAccess(result.device, id);
      await refreshAccess();
    } catch (e) {
      setError(e.message || 'Could not remove that user.');
    } finally {
      setAccessBusy(false);
    }
  };

  const doUnassign = async () => {
    // Checked against the SESSION record rather than the server: there is no
    // "verify my password" endpoint, and this is a mis-click guard, not an
    // authentication boundary — the page is already Super Admin only.
    if (!pw || String(pw) !== String(me?.Password ?? '')) {
      setPwError('That is not your password.');
      return;
    }
    setSaving(true);
    setPwError('');
    try {
      await unassignDeviceFully(result.device, uno.trim(), { userId });
      setUnassignOpen(false);
      setPw('');
      setSaved('Device unassigned.');
      await search(uno.trim(), { refresh: true });
    } catch (e) {
      setPwError('Could not unassign. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">This page is for administrators.</Alert>
      </Box>
    );
  }

  const d = result?.device;
  // Compact fact list. Short labels — the values say what they are, and a
  // column of full sentences is the bulk this page had too much of.
  //
  // A third value is an optional tooltip: two of these dates say something
  // narrower than their label suggests, and that is worth one hover rather than
  // a paragraph on screen.
  const facts = d ? [
    ['Installed Id', d.ID],
    ['Type', `${d.DeviceTypeName || '—'} · ID ${d.DeviceID}`],
    ['Outputs', d.DeviceOutputs ?? d.Outputs],
    // The owner's MOBILE as well as their name: two of these customers share a
    // name with somebody else, and the number is what an installer has to hand.
    ['Owner', [d.DeviceMasterUserName || d.DeviceMasterUser, d.MasUserMobileNumber]
      .filter(Boolean).join(' · ') || '—'],
    ['Location', d.LocationName || '—'],
    ['Room', d.RoomName || (Number(d.RoomId) === COMMON_ROOM_ID ? 'Common device' : '—')],
    // The network the device is JOINED TO, reported by the firmware itself on
    // every status update — not a plan, not the location's configured SSID.
    ['Wi‑Fi', d.WifiName || '—',
      'The SSID the device reports it is connected to. Sent with every status'
      + ' update, so it is as fresh as the last thing the device said.'],
    ['IP', d.DeviceIp || '—'],
    ['Last seen', hasNoHeartbeat(d) ? 'Bluetooth — no Wi-Fi heartbeat' : <When raw={d.LastUpdatedDateTime} />,
      'The last time anything wrote this device’s row.'],
    ['Last MQTT ping', hasNoHeartbeat(d) ? '—' : <When raw={d.LastMqttPing} />,
      'The last time the device itself spoke to the broker.'],
    ['Code updated', <When raw={d.CodeUpdated} />,
      'When the device last STARTED an update — it stamps this before flashing,'
      + ' so an image the bootloader then rejected still sets it. “What is it'
      + ' running?” is the only thing that settles what is actually on the chip.'],
    // Four different clocks, and they answer four different questions. They were
    // being read as interchangeable, which is how "last used" turned into an
    // argument: `LastUpdatedDateTime` moves on ANY write to the row, including
    // ours, so it is the one that least describes the device.
    ['Last status', <When raw={d.LastStatusTime ?? d.LastMqttPing} />,
      'The last status the device actually reported — the reading behind the'
      + ' Status line below.'],
    ['Status', d.DeviceStatus || d.Status || '—',
      'The last frame verbatim, [uno]values#source. The source says who caused'
      + ' it: a physical press, the app, or the box itself.'],
    ['Mapping', d.DeviceMappingStatus || '—',
      'The server’s own verdict on whether this device is attached to anybody.'],
  ] : [];

  // Everything else the reply carries, behind a toggle. The lookup returns ~100
  // columns; eleven of them answer the daily question and the rest are needed
  // perhaps once a month — but when they are needed, the alternative is a
  // database client. Grouped, because a flat list of ninety values is not
  // information.
  const moreFacts = d ? [
    ['Identity', [
      ['Unique number', d.UniqueNumber],
      ['Device name', d.DeviceName],
      ['Alexa name', `${d.AlexaName || '—'}${d.AlexaType ? ` · ${d.AlexaType}` : ''}`],
      ['Device password', d.DevicePassword],
      ['Company', d.CompanyId],
      ['Channel partner', d.ChannelPartnerId],
    ]],
    ['Type', [
      ['Device type', `${d.DeviceTypeName || '—'}${d.DeviceType ? ` · ${d.DeviceType}` : ''}`],
      ['Outputs / On_off / Dimming', `${d.Outputs ?? '—'} / ${d.On_off ?? '—'} / ${d.Dimming ?? '—'}`],
      ['Inputs', d.Inputs],
      ['Default value', d.DefaultValue],
      ['Master device', String(d.IsMasterDevice) === '1' ? 'yes' : 'no'],
      ['Current sensor', String(d.CurrentSensor) === '1' ? 'yes' : 'no'],
    ]],
    ['Firmware', [
      ['Bin file', d.BinFileName],
      ['Bin id', d.OTABinFileId],
      ['Build stamped', <When raw={d.HexFileDateTime} />],
      ['UART pins', d.UartPins],
      ['Require update', String(d.RequireUpdateCode) === '1' ? 'YES' : 'no'],
      // 0 on all 1,809 rows — a standing subscription, not a one-shot. Shown
      // because if it is ever 1 the device reflashes on every future publish.
      ['Auto update', String(d.RequireAutoUpdateCode) === '1' ? 'YES — reflashes on every publish' : 'no'],
      ['Pending command', d.PendingCommand],
    ]],
    ['Tuya (MCU link)', [
      ['Product id', d.TuyaProductIds],
      ['Switch DPs', d.TuyaSwitchDps],
      ['Fan DP / speed DP', `${d.TuyaFanDps ?? '—'} / ${d.TuyaFanSpeedDps ?? '—'}`],
      ['Master DP', d.TuyaMasterDp],
      ['Memory / child lock DP', `${d.TuyaMemoryDp ?? '—'} / ${d.TuyaChildLockDp ?? '—'}`],
    ]],
    ['Production', [
      ['Product stage', d.ProductStage],
      ['Packed on', <When raw={d.PackingDate} />],
      ['Blank PCB / assembled', `${d.BlankPcb ?? '—'} / ${d.Assembled ?? '—'}`],
      ['Tested / packed', `${d.Tested ?? '—'} / ${d.Packed ?? '—'}`],
      ['Authorized', String(d.authorized) === '1' ? 'yes' : 'no'],
      ['Device lock', String(d.DeviceLock) === '1' ? 'LOCKED' : 'no'],
    ]],
  ] : [];

  // Fields are sized to their CONTENT, not stretched to the panel. A room name
  // is a couple of words; a full-width box for it just pushes everything else
  // down the page.
  /*
   * Field widths — sized to their CONTENT, not to the column.
   *
   * These were wide enough that two of them would not sit side by side in the
   * Assign panel, so every field wrapped onto its own line and the panel grew a
   * scrollbar over a column that was half empty. A room name is a word; a
   * location name is two. They do not need 190px each.
   */
  // Widths for the fields that live in the TOP BAR, which is one long row and
  // genuinely does want fixed sizes.
  const W = { user: 190, loc: 150, room: 130, dev: 200, uno: 150, name: 160, bin: 260 };

  /*
   * ── The Assign panel packs itself ──────────────────────────────────────────
   *
   * ⚠️ FIXED PIXEL WIDTHS IN A FLUID COLUMN LOSE BOTH WAYS, which is what the
   * screenshot showed: "Jhanglani Bharat…", "Bharat Jh…" and "Bedro…" all
   * truncated, and ~200px of the panel left blank to the right of them. The
   * fields could not shrink enough to be honest, and could not grow to use what
   * was there.
   *
   * `auto-fit` + `minmax` fixes both at once: the row takes as many columns as
   * fit at 168px, then every one of them stretches to share the remainder. Two
   * fields on a narrow panel, four on a wide one, and never a gap at the end.
   * Fields that appear conditionally (New location, New room) just join the
   * flow instead of pushing a new part-empty row.
   *
   * The children set `width: '100%'` because MUI's TextField and Autocomplete
   * carry their own widths, which would otherwise win over the grid track.
   */
  const FIELDS = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(168px, 1fr))',
    gap: 1,
    alignItems: 'start',
  };
  const FULL = { width: '100%' };

  const typeChanged = d && deviceType !== '' && String(deviceType) !== String(d.DeviceID);
  const typeName = (types.find((t) => String(t.DeviceID) === String(deviceType)) || {}).DeviceName || '';

  // The device's owner is listed but cannot be removed here — dropping the
  // owner's own mapping would leave a device with an owner who cannot see it.
  // Unassign is the way to change ownership.
  const ownerId = d ? Number(d.MasterUserId) : null;
  const accessRows = access
    .map((id) => ({ id, u: users.find((x) => Number(x.UserId) === Number(id)) }))
    .sort((a, b) => (a.id === ownerId ? -1 : b.id === ownerId ? 1 : 0));

  return (
    // ⚠️ ONE SCREEN, NO PAGE SCROLL — and the panels scroll instead.
    //
    // This page used to be a 1040px column down the middle of a 1350px window:
    // 300px of empty gutter on either side while the firmware panel was pushed
    // below the fold. On an admin bench that means scrolling away the device's
    // facts to press a firmware button and back again to read the result of it,
    // which is exactly the pair that has to be read together.
    //
    // So: full width, a fixed-height flex column, and every panel a scroller of
    // its own (`minHeight: 0` — without it a flex child refuses to shrink below
    // its content and the page grows a scrollbar again).
    <Box sx={{
      height: '100%', p: 1, display: 'flex', flexDirection: 'column', gap: 1,
      overflow: 'hidden',
      ...COMPACT,
    }}
    >
      {/* No page title: the menu entry already said where you are, and a
          heading plus a subtitle cost a third of the fold for nothing. */}
      <Paper variant="outlined" sx={{ p: 1, flex: '0 0 auto' }}>
        {/* ⚠️ THE ROW SHARES ITS WIDTH INSTEAD OF HOLDING FIXED SLOTS. Every
            picker was a fixed 130-200px, so on a wide window the names were
            truncated to "Ailani Sanjay Har…" and "Sanjay Ailan" while the
            space after the Assigned chip sat empty. They now grow into it, in
            proportion to how long their contents actually are — a full name
            needs more than a room called "Office". */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Picker
            grow label="User" width={W.user} options={userOptions(users)}
            value={bUser ? bUser.UserId : ''}
            onChange={(id) => setBUser(users.find((u) => String(u.UserId) === String(id)) || null)}
          />
          <Picker
            grow label="Location" width={W.loc} options={locationOptions(bLocations)}
            value={bLocationId} onChange={setBLocationId} disabled={!bUser}
          />
          <Picker
            grow label="Room" width={W.room} disabled={!bLocationId}
            value={bRoomId} onChange={setBRoomId}
            options={[{ id: COMMON_ROOM_ID, label: 'Common devices' }, ...roomOptions(bRooms)]}
          />
          <Picker
            grow label="Device" width={W.dev} options={deviceOptions(bDevices)}
            value={bDeviceUno} onChange={pickDevice}
            disabled={bRoomId === '' || bDevices.length === 0}
          />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* The number is the fastest way in and the one field an operator
              types into all day, so it reads as the primary control: a little
              wider than a picker, monospaced so 8s and Bs are distinguishable,
              and paired with Find rather than floating beside it. */}
          <TextField
            size="small" label="Unique Number" value={uno}
            inputRef={unoRef}
            // Typing a number by hand un-names the browse selection: the moment
            // the two differ, the Device select is describing something other
            // than what the page is about to show.
            onChange={(e) => {
              setUno(e.target.value);
              if (e.target.value !== bDeviceUno) setBDeviceUno('');
            }}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            sx={{
              flex: `1 1 ${W.uno}px`,
              minWidth: 130,
              maxWidth: 240,
              '& .MuiInputBase-input': { fontFamily: 'monospace', letterSpacing: 0.5 },
            }}
          />
          <Button
            variant="contained" size="small" startIcon={<SearchIcon />}
            onClick={() => search()} disabled={searching || !uno.trim()}
            sx={{ flex: '0 0 auto', minWidth: 84 }}
          >
            {searching ? <CircularProgress size={16} /> : 'Find'}
          </Button>
          {result && (
            <Chip
              size="small"
              color={!result.found ? 'error' : result.assigned ? 'success' : 'warning'}
              label={!result.found ? 'Not found'
                : result.assigned ? 'Assigned' : 'Unassigned'}
            />
          )}
          {/* Messages ride in this row rather than owning one of their own —
              a band that is empty most of the time still costs its height on
              every paint. */}
          {error && <Alert severity="error" sx={{ py: 0, flex: '1 1 260px' }}>{error}</Alert>}
          {saved && <Alert severity="success" sx={{ py: 0, flex: '1 1 260px' }}>{saved}</Alert>}
          {result && !result.found && (
            <Alert severity="info" sx={{ py: 0, flex: '1 1 260px' }}>
              Not registered. It has to be created in the manufacturing system first.
            </Alert>
          )}
        </Box>
      </Paper>

      {d && (
        <Box sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gap: 1,
          alignItems: 'stretch',
          // Three columns once there is room for them, and the widest goes to
          // Firmware — it carries the served URL and the device's own OTA log,
          // which are the only monospace lines here.
          /*
           * ⚠️ THE COLUMNS ARE SIZED BY WHAT IS IN THEM, not evenly.
           *
           *   1  a label/value readout — the widest line is a timestamp, so it
           *      needs about 240px and was given 280+ plus a 0.85 share of the
           *      slack. The rest was blank paper.
           *   2  assign + Wi-Fi — the one that has to hold TWO fields side by
           *      side, or every field wraps onto its own row and the panel grows
           *      a scrollbar over a half-empty column.
           *   3  firmware — carries the served OTA URL and the device's own log,
           *      the only long unbreakable strings on the page.
           */
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            // ⚠️ Weighted by how much each column has to SAY, not evenly. Assign
            // carries seven fields and grows to four per row here; Firmware is a
            // URL that wraps and four controls, and was holding width it left
            // blank. Device is a two-column readout and needs the least.
            lg: 'minmax(215px, 0.55fr) minmax(420px, 1.35fr) minmax(300px, 0.95fr)',
          },
          gridAutoRows: { xs: 'auto', lg: 'minmax(0, 1fr)' },
          overflow: { xs: 'auto', lg: 'hidden' },
        }}
        >
          {/*
            ⚠️ THIS COLUMN SCROLLS. It used to only `minHeight: 0` inside a grid
            whose row is a fixed fraction of the viewport with `overflow:
            hidden` — so anything taller than the cell was CLIPPED with no
            scrollbar and no indication. On a curtain it swallowed the Controls
            card outright: the panel that answers "does this device work" was
            the part cut off, on the screen you open when it does not.

            Height is not predictable here — the facts table grows with the
            device, and each control card is a different height — so the fix is
            to let the column scroll rather than to keep guessing at heights.
          */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minHeight: 0,
            overflowY: { xs: 'visible', lg: 'auto' },
            // Room for the scrollbar so it does not sit over the content.
            pr: { lg: 0.5 },
          }}
          >
            <Paper variant="outlined" sx={{ p: 1.25, flex: '0 0 auto' }}>
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Device
              </Typography>
              {/* ⚠️ `tableLayout: fixed` + a narrow label column. Auto layout
                  sizes columns to their widest CELL, so "Last MQTT ping" was
                  reserving its own width in every row and the values sat in a
                  column of their own with the rest of the paper left blank.
                  Fixed layout hands the remainder to the values, and the label
                  wraps to two lines where it needs to. */}
              <Table size="small" sx={{ tableLayout: 'fixed', '& td': { border: 0, py: 0.15 } }}>
                <TableBody>
                  {facts.map(([k, v, tip]) => (
                    <TableRow key={k}>
                      {/* ⚠️ 74 px WAS TOO NARROW, and narrow cost height. At
                          that width "Last MQTT ping" wrapped to three lines and
                          "Code updated" to two, so five of the eleven rows were
                          double-height — the label column saved 14 px of width
                          and spent about 60 px of the column's height on it, in
                          a panel whose problem is height. */}
                      <TableCell sx={{
                        color: 'text.secondary', width: 88, fontSize: 11, pr: 0.5, verticalAlign: 'top',
                      }}
                      >
                        {tip ? (
                          <Tooltip title={tip} placement="left">
                            <Box component="span" sx={{ borderBottom: '1px dotted', cursor: 'help' }}>
                              {k}
                            </Box>
                          </Tooltip>
                        ) : k}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, pl: 0, wordBreak: 'break-word' }}>
                        {v === null || v === undefined || v === '' ? '—' : v}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Button
                size="small" variant="text" onClick={() => setShowMore((v) => !v)}
                sx={{ textTransform: 'none', mt: 0.5, py: 0 }}
              >
                {showMore ? 'Hide the rest' : 'All details…'}
              </Button>

              {showMore && moreFacts.map(([group, rows]) => (
                <Box key={group} sx={{ mt: 0.75 }}>
                  <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', fontSize: 10, lineHeight: 1.4 }}
                  >
                    {group}
                  </Typography>
                  <Table size="small" sx={{ '& td': { border: 0, py: 0.1 } }}>
                    <TableBody>
                      {rows.map(([k, v]) => (
                        <TableRow key={k}>
                          <TableCell sx={{ color: 'text.secondary', width: 148, fontSize: 11 }}>
                            {k}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11.5, wordBreak: 'break-all' }}>
                            {v === null || v === undefined || v === '' ? '—' : v}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </Paper>

            {/*
              ── CONTROLS: the device itself, on the admin screen ─────────────
              Everything else here is ABOUT the device — who owns it, what
              firmware it runs, which Wi-Fi it joined. None of it answers the
              question you actually came with, which is "does it work?". You had
              to note the unique number, leave, find the device in a room and
              press something.

              So the real control is here, chosen by DeviceID through the same
              `DeviceControlCard` the room grid uses — a 4-touch gets its four
              buttons and All On/Off, a fan (53) gets its speed dial, an IR
              blaster (19) gets its shortcuts and the full remote. No new UI and
              no second implementation to drift: fix a control once and it is
              fixed in the room, on the wall panel and here.

              ⚠️ It publishes for REAL. This is a bench tool on live hardware —
              pressing a key here switches somebody's light, so it is labelled
              as the device's controls rather than as a "test".
            */}
            {/* ⚠️ NOT wrapped in a Paper. DeviceControlCard is already a bordered,
                padded Card, and nesting it inside another one drew two frames
                with a gutter between them and cost ~20px of width for nothing.
                The heading sits directly above the card instead. */}
            {d.DeviceID != null && (
              <Box sx={{ flex: '0 0 auto' }}>
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Controls
                </Typography>
                <DeviceControlCard device={d} />
              </Box>
            )}

            {/* ⚠️ `flex-shrink: 0` and a floor. In a column that now scrolls,
                `1 1 auto` let this be squeezed to nothing by the two panels
                above it — the access list would vanish rather than the column
                growing a scrollbar, which is the same disappearing act in a
                different place. */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.25, flex: '1 0 auto', minHeight: 120, display: 'flex', flexDirection: 'column',
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {`Users with access (${accessRows.length})`}
              </Typography>

              <Box sx={{
                display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1,
                flex: '1 1 auto', minHeight: 0, overflowY: 'auto', alignContent: 'flex-start',
              }}
              >
                {accessRows.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nobody but the owner can see this device.
                  </Typography>
                )}
                {accessRows.map(({ id, u }) => (
                  <Chip
                    key={id}
                    size="small"
                    label={u ? `${userLabel(u)} · ${u.MobileNumber || id}` : `User ${id}`}
                    color={id === ownerId ? 'primary' : 'default'}
                    variant={id === ownerId ? 'filled' : 'outlined'}
                    onDelete={id === ownerId ? undefined : () => revoke(id)}
                    disabled={accessBusy}
                  />
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: '0 0 auto' }}>
                <Picker
                  label="Add user" width={W.user}
                  value={addUserId} onChange={setAddUserId}
                  options={userOptions(users.filter((u) => !access.includes(Number(u.UserId))))}
                  helperText="Also grants this location and room — without them the device stays invisible."
                />
                <Button
                  size="small" variant="outlined" startIcon={<PersonAddIcon />}
                  onClick={() => grant(addUserId)}
                  disabled={!addUserId || accessBusy} sx={{ height: 40 }}
                >
                  Give access
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* Column 2: Assign, then the device's own Wi-Fi list. Stacked in one
              grid cell rather than added as a fourth column — three is already
              the widest this page lays out, and a fourth would wrap under
              Device and read as a new row of unrelated things. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
          <Paper
            variant="outlined"
            /*
             * ⚠️ NATURAL HEIGHT, AND NO SCROLLBAR OF ITS OWN. This was a flex
             * child with `minHeight: 0` and `overflow: auto`, so it was given
             * an arbitrary share of the column and everything past it — Device
             * type, and the whole Address / City / Pincode block — was hidden
             * below a fold nobody looks for inside a panel. A form does not
             * shrink: it is as tall as its fields, and the list BELOW it takes
             * whatever is left.
             */
            sx={{ p: 1.25, flex: '0 0 auto' }}
          >
            {/* Title and actions on ONE row. The Save/Unassign pair used to sit
                on a row of its own at the bottom of the panel, which cost a
                whole line of a column that is already the tightest on the page.
                As icons in the header they take no extra height at all.

                ⚠️ Both keep a tooltip, and Save's SAYS WHY IT IS DISABLED. An
                icon with no label and no reason is the version of this that
                caused the report: the button was grey because Room was empty,
                and nothing on screen said so. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Assign
              </Typography>
              <Box sx={{ flex: 1 }} />
              {result.assigned && (
                <Tooltip title="Unassign this device">
                  <span>
                    <IconButton
                      size="small" color="error" disabled={saving}
                      onClick={() => { setPw(''); setPwError(''); setUnassignOpen(true); }}
                    >
                      <LinkOffIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {/* ⚠️ THE BUTTON IS THE UNSAVED-CHANGES INDICATOR. It appears
                  only when the panel differs from the stored row, so a Save
                  sitting there means "there is something to write" rather than
                  being permanent furniture that says nothing either way. Its
                  absence is the answer to "did that go through?".

                  An unassigned device always shows it — nothing about it is
                  stored yet — but disabled until the fields are filled, with
                  the reason in the tooltip. */}
              {assignDirty ? (
                <Tooltip title={canSave
                  ? 'Save the assignment  ·  Ctrl+S  or  Ctrl+Shift+Enter'
                  : (whyCannotSave || 'Nothing to save')}
                >
                  {/* A disabled button fires no events, so the tooltip needs a
                      wrapper that can — otherwise the explanation never shows,
                      which is exactly when it is wanted. */}
                  <span>
                    <IconButton
                      size="small" color="primary" onClick={save}
                      disabled={!canSave || saving}
                    >
                      {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Saved — the panel matches the device">
                  <CheckCircleIcon
                    fontSize="small"
                    sx={{ color: 'success.main', opacity: 0.85, mx: 0.5 }}
                  />
                </Tooltip>
              )}
            </Box>

            {/* ⚠️ SAYS THAT THIS CAME FROM THE LAST DEVICE, NOT FROM THIS ONE.
                A pre-filled owner and room are indistinguishable from a device
                that is already placed, and mistaking one for the other is how a
                whole box gets assigned to the flat before it. It clears itself
                the moment anything is changed or saved. */}
            {carried && (
              <Alert
                severity="info" icon={false}
                sx={{ mt: 0.5, py: 0, '& .MuiAlert-message': { py: 0.5 } }}
                action={(
                  <Button
                    size="small" color="inherit" sx={{ textTransform: 'none' }}
                    onClick={() => {
                      setSticky(null);
                      saveSticky(null);
                      setCarried(false);
                      setUser(null);
                      pending.current = null;
                      setPreselect((n) => n + 1);
                    }}
                  >
                    Clear
                  </Button>
                )}
              >
                <Typography variant="body2">
                  Carried over from the last device you assigned — this one is
                  unassigned. Change any of it before saving.
                </Typography>
              </Alert>
            )}

            {/* The device IS somewhere — the facts table says where — but that
                somewhere is not in this user's list, so the select below is
                blank. Said out loud, because a blank select reads as "this
                device has no room" and the next thing the operator does is pick
                one, which MOVES the device. Usually a missing user->location or
                user->room mapping rather than a wrong assignment. */}
            {preselectMiss && (
              <Alert
                severity="warning"
                sx={{ mt: 0.5, py: 0, '& .MuiAlert-message': { py: 0.5 } }}
              >
                <Typography variant="body2">
                  {preselectMiss === 'location'
                    ? `The device is in ${d.LocationName || `location ${d.LocationID ?? d.LocationId}`}, which is not one of ${user ? userLabel(user) : 'this user'}’s locations — so nothing is preselected. Saving a different one MOVES the device.`
                    : `The device is in ${d.RoomName || `room ${d.RoomId}`}, which is not in this location’s room list for ${user ? userLabel(user) : 'this user'} — so no room is preselected. Saving a different one MOVES the device.`}
                </Typography>
              </Alert>
            )}

            {/* Two fields per row, at their own widths — see the note on W.
                They wrapped one-per-row before, which is what grew the
                scrollbar on a column that was half empty. */}
            <Box sx={{ ...FIELDS, mt: 0.5 }}>
              <TextField
                size="small" label="Device name" value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                sx={FULL}
              />
              <Picker
                label="Master user" width="100%"
                options={[...userOptions(users), { id: NEW, label: '＋ New user…' }]}
                value={userIsNew ? NEW : (user ? user.UserId : '')}
                onChange={(id) => {
                  if (id === NEW) { setUserIsNew(true); setUser(null); return; }
                  setUserIsNew(false);
                  setUser(users.find((u) => String(u.UserId) === String(id)) || null);
                }}
                // The number is how an installer identifies a customer on the
                // phone, and it is also the only thing separating two people
                // with the same name — of which this list has sixteen sets.
                helperText={user ? `${user.MobileNumber || 'no mobile'} · ID ${user.UserId}` : ' '}
              />
            </Box>

            {/* A brand-new owner. Shown only on demand, because it is four more
                fields on a panel that is already dense — and because the usual
                case is a device going to somebody who already exists. */}
            {userIsNew && (
              <Box sx={{
                ...FIELDS, mt: 1, p: 1, borderRadius: 1,
                border: '1px dashed', borderColor: 'divider',
              }}
              >
                <TextField
                  size="small" label="Surname" sx={FULL} value={nu.surname}
                  onChange={(e) => patchNu({ surname: e.target.value })}
                />
                <TextField
                  size="small" label="Name" sx={FULL} value={nu.name}
                  onChange={(e) => patchNu({ name: e.target.value })}
                />
                <TextField
                  size="small" label="Father / Husband" sx={FULL} value={nu.fatherHusband}
                  onChange={(e) => patchNu({ fatherHusband: e.target.value })}
                />
                <TextField
                  size="small" label="Mobile" sx={FULL}
                  value={nu.mobileNumber}
                  onChange={(e) => patchNu({ mobileNumber: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  error={!!mobileTaken}
                  // ⚠️ Not a nicety. AddNewUser is an upsert on this number, so a
                  // digit wrong here rewrites a real customer's name and PIN and
                  // reports success.
                  helperText={mobileTaken
                    ? `Already ${userLabel(mobileTaken)} — pick them from the list`
                    : '10 digits — this is their login'}
                />
                <TextField
                  size="small" label="PIN" sx={FULL}
                  value={nu.password}
                  onChange={(e) => patchNu({ password: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  helperText="4 digits"
                />
              </Box>
            )}

            <Box sx={{ ...FIELDS, mt: 1 }}>
              <Picker
                label="Location" width="100%" value={locationId} onChange={setLocationId}
                // A brand-new owner has no locations to list, but must still be
                // able to create one — this is the "everything is new" path.
                disabled={!user && !userIsNew}
                options={[...locationOptions(locations), { id: NEW, label: '＋ New location…' }]}
              />
              {locationId === NEW && (
                <TextField
                  size="small" label="New location" value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)} sx={FULL}
                  helperText="The site's name — “Home”, “Shop”"
                />
              )}
              <Picker
                label="Room" width="100%" value={roomId} onChange={setRoomId}
                disabled={!locationId}
                options={[
                  // Room 0 is a real assignment, not a blank — the device
                  // belongs to the location and shows under Common Devices.
                  { id: COMMON_ROOM_ID, label: 'Common device' },
                  ...roomOptions(rooms),
                  { id: NEW, label: '＋ New room…' },
                ]}
              />
              {roomId === NEW && (
                <TextField
                  size="small" label="New room" value={newRoom}
                  onChange={(e) => setNewRoom(e.target.value)} sx={FULL}
                />
              )}
            </Box>

            {/* Actions, not fields — kept out of the grid so a button never
                takes a whole column that a name could have used. */}
            {selectedRoom && (
              <Button
                size="small" variant="text"
                sx={{ textTransform: 'none', py: 0, mt: 0.25 }}
                onClick={() => { setRoomNewName(selectedRoom.RoomName || ''); setRenaming(true); }}
              >
                {`Rename “${selectedRoom.RoomName}”`}
              </Button>
            )}

            {/* ── The new location's address ─────────────────────────────────
                Only while a location is being created — an existing one is
                edited on the Location screen, and repeating its fields here
                would be a second place to change the same row from.

                ADD1 is not a filing detail: the mobile room list prints it as
                the subtitle under the location's name, so a site saved without
                one reads as blank there. */}
            {locationId === NEW && (
              <Box sx={{
                ...FIELDS, mt: 1, p: 1, borderRadius: 1,
                border: '1px dashed', borderColor: 'divider',
              }}
              >
                <TextField
                  size="small" label="Address 1" sx={FULL} value={addr.add1}
                  onChange={(e) => patchAddr({ add1: e.target.value })}
                  helperText="Shown under the location name in the app"
                />
                <TextField
                  size="small" label="Address 2" sx={FULL} value={addr.add2}
                  onChange={(e) => patchAddr({ add2: e.target.value })}
                />
                <TextField
                  size="small" label="City" sx={FULL} value={addr.city}
                  onChange={(e) => patchAddr({ city: e.target.value })}
                />
                <TextField
                  size="small" label="Pincode" sx={FULL} value={addr.pincode}
                  onChange={(e) => patchAddr({ pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  helperText="6 digits"
                />
              </Box>
            )}

            <Box sx={{ ...FIELDS, mt: 1 }}>
              <Picker
                label="Device type" width="100%" value={deviceType}
                onChange={setDeviceType}
                // ⚠️ The TYPES table calls the type's name `DeviceName`, while
                // an installed device's row calls the same thing
                // `DeviceTypeName`. Reading DeviceTypeName here yields
                // undefined and every option falls back to "Type 49".
                options={types.map((t) => ({
                  id: t.DeviceID,
                  label: t.DeviceName || `Type ${t.DeviceID}`,
                  sub: `${t.DeviceType || ''} · ID ${t.DeviceID}`.replace(/^ · /, ''),
                }))}
              />
            </Box>

            {/* Changing the type usually means the name is now wrong ("4
                TOUCH" on a device that is no longer a 4-touch). Offered, not
                automatic: plenty of devices are deliberately named after the
                room or the load rather than the hardware. Outside the grid, so
                it never takes a column a field could have used. */}
            {typeChanged && typeName && deviceName !== typeName && (
              <Button
                size="small" variant="text"
                sx={{ textTransform: 'none', py: 0, mt: 0.25 }}
                onClick={() => setDeviceName(typeName)}
              >
                {`Rename device to “${typeName}”`}
              </Button>
            )}

          </Paper>

          {/* ── The device's OWN saved networks ─────────────────────────────
              Asked of the DEVICE over MQTT (`getallwifi`), not read from the
              database: this is the list the firmware will actually try, in the
              order it will try them, which is the only list worth seeing when a
              device will not come online. The Wi-Fi row in Device facts above is
              a different fact — the ONE network it is currently joined to.

              ⚠️ Needs the device to be online. It is a round trip with a
              timeout, and a device that is off answers nothing — the panel says
              so rather than showing an empty list, which would read as "this
              device has no networks saved". */}
          {/* The saved-network list is unbounded, so it is the part that gets
              the leftover height and the scrollbar. */}
          <Paper variant="outlined" sx={{ p: 1.25, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Wi‑Fi networks on this device
            </Typography>
            <DeviceWifiPanel device={{ UniqueNumber: d.UniqueNumber }} />
          </Paper>
          </Box>

          {/* ── Firmware ───────────────────────────────────────────────────
              Deliberately NOT part of the Assign save above. That one needs an
              owner, a location and a room; this one needs none of them, and the
              devices that need it most have none — a bench board is unowned and
              unplaced, which is exactly this device. Tying firmware to the
              assignment would put it out of reach for its main use. */}
          <Paper
            variant="outlined"
            sx={{ p: 1.25, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Firmware
            </Typography>

            {/* What the DEVICE will fetch, from the call the firmware itself
                makes — not what any table says. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Downloads now:
              {' '}
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                {servedNow || '—'}
              </Box>
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
              <Picker
                label="OTA bin file" width={W.bin} value={binPick} onChange={setBinPick}
                options={bins.map((b) => ({ id: b.OTABinFileId, label: b.BinFileName }))}
              />
              <Button
                size="small" variant="outlined" startIcon={<SaveIcon />}
                onClick={saveBin}
                disabled={fwBusy || binPick === '' || String(binPick) === String(d.OTABinFileId ?? '')}
              >
                Set &amp; verify
              </Button>

              <Box sx={{ width: 12 }} />

              {/* Publishes UPDATE CODE now — and, if nothing answers, falls
                  back to the pending message below by itself, because a publish
                  a device is not listening for is dropped with no error. */}
              <Button
                size="small" variant="outlined" color="warning"
                startIcon={<SystemUpdateAltIcon />}
                onClick={sendUpdate} disabled={fwBusy}
                title={`Sends it now. If nothing answers within ${Math.round(OTA_SILENCE_MS / 1000)}s,`
                  + ' the update is parked for the device\'s next poll instead.'}
              >
                Upload code
              </Button>
              {/* After an update, this is what says whether it took. */}
              <Button
                size="small" variant="outlined"
                onClick={checkRunning} disabled={fwBusy}
              >
                What is it running?
              </Button>
            </Box>

            {fwMsg && (
              <Alert
                severity={fwMsg.severity} sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.75 } }}
              >
                <Typography variant="body2">{fwMsg.text}</Typography>
              </Alert>
            )}

            {/* Every line the device publishes about the update, verbatim. The
                ack is greyed: it is sent even when the update did NOT start.
                This is the one part of the page that grows without limit, so it
                is the part that takes the leftover height and scrolls. */}
            {otaLines.length > 0 && (
              <Box sx={{ mt: 1, flex: '1 1 auto', minHeight: 40, overflowY: 'auto' }}>
                {otaLines.map((e, i) => (
                  <Typography
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all',
                      opacity: e.kind === OTA_ACK ? 0.5 : 1,
                    }}
                  >
                    {e.text}
                  </Typography>
                ))}
              </Box>
            )}

            {/* ── Pending message ─────────────────────────────────────────
                The other route: the device polls the server every 20 minutes
                and runs whatever is parked here, so this reaches a device that
                is not listening now — which MQTT cannot do. */}
            <Box sx={{
              display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1.5,
              flex: '0 0 auto',
            }}
            >
              <TextField
                size="small" label="Pending message" value={pendingCmd}
                onChange={(e) => setPendingCmd(e.target.value)}
                sx={{ width: 260 }}
                placeholder="UPDATE CODE"
                // ⚠️ One slot, not a queue: saving replaces whatever was there,
                // and an unrecognised command fails silently at the device.
                helperText="Run on the device's next poll (~20 min). Replaces anything already pending."
              />
              <Button
                size="small" variant="outlined" startIcon={<SaveIcon />}
                onClick={savePending} disabled={fwBusy}
                sx={{ mt: -2.5 }}
              >
                Save
              </Button>
              {/*
                ⚠️ MARK, NOT SEND — the difference from "Upload code" above.
                That one publishes over MQTT and only reaches a device that is
                listening RIGHT NOW; this writes BOTH server-side routes on the
                row, and the firmware finds them by itself. They are the only
                way to a device that is asleep or unplugged, which is usually
                the device somebody opened this screen about.

                A button rather than "type UPDATE CODE and press Save": the
                exact word matters, and typing it by hand is where a silent typo
                comes from — an unrecognised command occupies the slot and does
                nothing, at both ends, with no error. The field cannot set the
                mark at all, only the parked command.
              */}
              <Tooltip title="Park “update code” AND set the update mark — it updates itself on its next check-in (~20 min) or its next power-up, even if it is offline now, and keeps trying until a flash succeeds">
                <span>
                  <Button
                    size="small" variant="contained" color="warning"
                    startIcon={<SystemUpdateAltIcon />}
                    onClick={markUpdateCode} disabled={fwBusy}
                    sx={{ mt: -2.5 }}
                  >
                    Mark update code
                  </Button>
                </span>
              </Tooltip>
              {/* ⚠️ THE MARK IS THE ONE THAT DOES NOT EXPIRE. A device that can
                  never take its image retries on every reconnect until this is
                  pressed, so the state and the way out both have to be visible
                  here rather than only in the database. */}
              {Number(d[UPDATE_MARK_COLUMN]) === 1 && (
                <Tooltip title={`${UPDATE_MARK_COLUMN} = 1. The device re-reads this on every Wi-Fi connect and clears it itself once a flash succeeds — so if it is still set, no flash has succeeded.`}>
                  <span>
                    <Button
                      size="small" variant="outlined" color="warning"
                      onClick={clearUpdateMark} disabled={fwBusy}
                      sx={{ mt: -2.5 }}
                    >
                      Clear update mark
                    </Button>
                  </span>
                </Tooltip>
              )}
              {d.PendingCommand ? (
                <Chip
                  size="small" color="info" variant="outlined"
                  label={`pending: ${d.PendingCommand}`}
                  sx={{ mt: -2.5 }}
                />
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ mt: -2.5 }}>
                  Nothing pending.
                </Typography>
              )}
              <Chip
                size="small"
                color={Number(d[UPDATE_MARK_COLUMN]) === 1 ? 'warning' : 'default'}
                variant="outlined"
                label={Number(d[UPDATE_MARK_COLUMN]) === 1 ? 'marked for update' : 'not marked'}
                sx={{ mt: -2.5 }}
              />
            </Box>
          </Paper>
        </Box>
      )}

      <Dialog open={renaming} onClose={() => setRenaming(false)} {...FIT_XS}>
        <DialogTitle sx={{ pb: 0.5 }}>Rename room</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Rooms are shared — everyone with access to this room sees the new
            name, not just this user.
          </Typography>
          <TextField
            size="small" fullWidth autoFocus label="Room name" value={roomNewName}
            onChange={(e) => setRoomNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doRenameRoom()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={doRenameRoom} disabled={saving || !roomNewName.trim()}>
            {saving ? <CircularProgress size={18} /> : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password-guarded unassign. Spells out exactly what will be cleared —
          "are you sure" tells the operator nothing they can weigh. */}
      <Dialog open={unassignOpen} onClose={saving ? undefined : () => setUnassignOpen(false)} {...FIT_XS}>
        <DialogTitle sx={{ pb: 0.5 }}>Unassign this device?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {`${d?.DeviceName || uno} will be detached from `}
            <b>{d?.DeviceMasterUserName || 'its owner'}</b>
            {`, ${d?.LocationName || 'its location'}`}
            {d?.RoomName ? ` / ${d.RoomName}` : ''}
            {'. Its owner, location, room and every user are cleared, and it is reset to a fresh piece: button names and settings, schedules, two-way links, scene steps and RF links are removed and its name returns to Smartify_' + uno.trim() + '.'}
          </Typography>
          {pwError && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{pwError}</Alert>}
          <TextField
            size="small" fullWidth type="password" autoFocus
            label="Your password" value={pw}
            onChange={(e) => { setPw(e.target.value); setPwError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && doUnassign()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnassignOpen(false)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doUnassign} disabled={saving || !pw}>
            {saving ? <CircularProgress size={18} /> : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
